import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  Link,
} from 'react-router-dom';
import { Pages, Public } from 'pages';
import { loginPagesLoading, loginIsPublic } from 'pages/Login';
import { useDispatch, useSelector } from 'react-redux';
import { LOADING, Sizes } from 'common';
import { Result, BackTop, Spin } from 'antd';
import { useMsal } from '@azure/msal-react';
import {
  EventType,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import {
  editAuthenticationPhoneRequest,
  editEmailPolicy,
  forgotPasswordPolicy,
  forgotPasswordRequest,
  forgotUsernameRequest,
  onDemandMFAPhonePolicy,
  profileEditRequest,
  profileViewPolicy,
  relativeEditAuthenticationPhoneUrl,
  relativeEditEmailPhoneUrl,
  relativeForgotPasswordUrl,
  relativeForgotUsernameUrl,
  relativeOnDemandMFAPhoneUrl,
  relativeProfileEditUrl,
  relativeProfileViewUrl,
  relativeResetPasswordUrl,
  resetPasswordRequest,
} from 'authConfig';
import { refreshAdUserSession } from 'common/helpers';
import { AppState } from 'store';
import globalStyles from 'common/global.module.scss';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { instance, inProgress } = useMsal();
  const login = useSelector((state: AppState) => state.login);
  const pagesLoading = login.pagesLoading;
  const isPublicUser: any = localStorage.getItem('ispublicuser') !== 'false';

  const doesNotExist = () => (
    <Result
      status="404"
      title="404"
      subTitle="Sorry, the page you visited does not exist."
      extra={<Link to="/pages">Go To Dashboard</Link>}
    />
  );

  const redirectToPolicy = (policy: any) => {
    const timer = setTimeout(() => {
      instance.loginRedirect(policy);
    }, 1);
    return () => clearTimeout(timer);
  };

  const preAuthorizationAction = () => {
    dispatch(loginPagesLoading(true));
  };

  const postAuthorizationAction = () => {
    dispatch(loginPagesLoading(false));
    dispatch(loginIsPublic(isPublicUser));
  };

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (
          event.error &&
          event.error.errorMessage.indexOf('AADB2C90118') > -1
        ) {
          if (event.interactionType === InteractionType.Redirect) {
            instance.loginRedirect(forgotPasswordRequest);
          } else if (event.interactionType === InteractionType.Popup) {
            instance.loginPopup(forgotPasswordRequest).catch((e) => {
              return;
            });
          }
        }
      } else if (event.eventType === EventType.HANDLE_REDIRECT_START) {
        if (window.location.pathname === relativeForgotPasswordUrl) {
          redirectToPolicy(forgotPasswordRequest);
        } else if (window.location.pathname === relativeForgotUsernameUrl) {
          redirectToPolicy(forgotUsernameRequest);
        } else if (window.location.pathname === relativeProfileViewUrl) {
          redirectToPolicy(profileViewPolicy);
        } else if (window.location.pathname === relativeProfileEditUrl) {
          redirectToPolicy(profileEditRequest);
        } else if (
          window.location.pathname === relativeEditAuthenticationPhoneUrl
        ) {
          redirectToPolicy(editAuthenticationPhoneRequest);
        } else if (window.location.pathname === relativeEditEmailPhoneUrl) {
          redirectToPolicy(editEmailPolicy);
        } else if (window.location.pathname === relativeResetPasswordUrl) {
          redirectToPolicy(resetPasswordRequest);
        } else if (window.location.pathname === relativeOnDemandMFAPhoneUrl) {
          redirectToPolicy(onDemandMFAPhonePolicy);
        }
      }

      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        if (event?.payload) {
          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          if (event.payload.idTokenClaims['acr'] === forgotPasswordPolicy) {
            window.alert(
              'Password has been reset successfully. \nPlease sign-in with your new password.',
            );
            localStorage.removeItem('user');
            localStorage.removeItem('refreshadusersession');
            localStorage.removeItem('ispublicuser');
            localStorage.removeItem('iswellsfargouser');
            return instance.logoutRedirect();
          }
        }
      }

      if (event.eventType === EventType.LOGIN_SUCCESS) {
        // call the authorize method on our API to confirm this user has appropriate permissions
        if (event?.payload?.account) {
          refreshAdUserSession(
            instance,
            event?.payload?.account,
            dispatch,
            postAuthorizationAction,
            preAuthorizationAction,
          );
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, []);

  return (
    <Spin
      spinning={pagesLoading}
      size={Sizes.LARGE}
      tip={LOADING}
      className={globalStyles.positionFixedImportant}
    >
      <Router basename={process.env.REACT_APP_BASENAME}>
        <BackTop />
        <Switch>
          {inProgress === InteractionStatus.None && !pagesLoading && (
            <Redirect from="/" to="/pages" exact={true} />
          )}
          <Route path="/pages" component={Pages} />
          <Route path="/public" component={Public} />
          {inProgress === InteractionStatus.None && !pagesLoading && (
            <Route component={doesNotExist} />
          )}
        </Switch>
      </Router>
    </Spin>
  );
};

export default React.memo(App);
